import type { Customer, Partner } from "@brookbush/types";

import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useState } from "react";

interface LandingPagePartnersProps {
	partners: Partner[];
	customers: Customer[];
}

export default function LandingPagePartners({
	partners,
	customers,
}: LandingPagePartnersProps) {
	const [selected, setSelected] = useState<Partner>();

	return (
		<section
			id="partners"
			className="relative w-full scroll-m-12 border-b bg-gradient-to-b from-white to-gray-50 dark:border-slate-800 dark:from-slate-800 dark:to-slate-900"
		>
			<div className="container mx-auto px-4 py-6 sm:py-12">
				<div className="mx-auto flex max-w-5xl flex-col gap-4 text-center">
					<div className="space-y-3 sm:space-y-4">
						<h2 className="font-bold text-2xl tracking-tight sm:text-4xl">
							Our Partners
						</h2>
						<p className="mx-auto text-muted-foreground text-sm sm:text-base">
							Optimizing education through partnerships with training and
							development and academic programs.
						</p>
					</div>

					<div className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-5">
						{partners.map((partner) => (
							<div
								key={partner.slug}
								onClick={() => setSelected(partner)}
								className="group relative isolate rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-100 transition-all hover:shadow-md dark:bg-slate-800 dark:ring-slate-700 dark:hover:bg-slate-700"
							>
								<div className="relative aspect-[3/2] w-full overflow-hidden">
									<Image
										src={partner.thumbnail?.url!}
										alt={partner.thumbnail?.alt! || partner.title}
										layout="fill"
										lazyBoundary="5px"
										className="object-contain transition duration-300 group-hover:scale-110 dark:brightness-110"
									/>
								</div>
							</div>
						))}
					</div>

					<div className="space-y-4 pt-4">
						<p className="text-muted-foreground text-sm sm:text-base">
							Proud to educate the best fitness, strength, and clinical
							professionals from marquee institutions.
						</p>

						<div className="grid grid-cols-4 gap-8 overflow-hidden sm:grid-cols-5 md:grid-cols-8 lg:grid-cols-10">
							{customers.map((customer) => (
								<div key={customer.title} className="group relative">
									<div className="relative aspect-square w-full overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-100 dark:bg-slate-800 dark:ring-slate-700">
										<Image
											src={customer.thumbnail.url}
											alt={customer.title}
											layout="fill"
											lazyBoundary="5px"
											className="object-contain px-0.5 py-1.5 transition duration-300 group-hover:scale-110 dark:brightness-110"
										/>
									</div>

									<div className="absolute top-0 left-0 z-10 mt-1 whitespace-nowrap opacity-0 transition-opacity duration-150 group-hover:opacity-100">
										<div className="rounded bg-white/90 p-1 shadow-sm ring-1 ring-gray-100/50 dark:bg-slate-800/90 dark:ring-slate-700/50">
											<p className="text-foreground/80 text-xs dark:text-slate-200/80">
												{customer.title}
											</p>
										</div>
									</div>
								</div>
							))}
						</div>

						<div className="pt-2 text-center sm:pt-4">
							<Link
								href="/team-membership"
								className="text-muted-foreground text-xs transition-colors hover:text-foreground sm:text-sm"
							>
								Team memberships →
							</Link>
						</div>
					</div>
				</div>
			</div>

			<AnimatePresence>
				{selected && (
					<Dialog
						static
						open={Boolean(selected)}
						onClose={() => setSelected(undefined)}
						className="relative z-50"
					>
						<motion.div
							className="fixed inset-0 bg-black/60 backdrop-blur-sm"
							aria-hidden="true"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
						/>

						<div className="fixed inset-0 flex items-center justify-center p-4">
							<motion.div
								initial={{ scale: 0.95, opacity: 0 }}
								animate={{ scale: 1, opacity: 1 }}
								exit={{ scale: 0.95, opacity: 0 }}
								transition={{ duration: 0.2 }}
								className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white p-6 shadow-xl sm:p-8 dark:bg-slate-800"
							>
								<button
									onClick={() => setSelected(undefined)}
									className="absolute top-4 right-4 rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
								>
									<X className="size-5" />
									<span className="sr-only">Close</span>
								</button>

								<div className="flex flex-col gap-6">
									<div className="relative mx-auto aspect-[3/2] w-full max-w-md">
										<Image
											src={selected.thumbnail?.url!}
											alt={selected.thumbnail?.alt!}
											layout="fill"
											lazyBoundary="5px"
											className="object-contain dark:brightness-110"
										/>
									</div>
									<div className="space-y-3 text-center">
										<Dialog.Title className="font-semibold text-2xl tracking-tight sm:text-3xl">
											{selected.title}
										</Dialog.Title>
										<Dialog.Description className="text-lg text-muted-foreground">
											{selected.description}
										</Dialog.Description>
									</div>
								</div>
							</motion.div>
						</div>
					</Dialog>
				)}
			</AnimatePresence>
		</section>
	);
}
